import React, { forwardRef, useState } from 'react';
import FixedProgramBulkTable, { type BulkClassOption, type FixedProgramBulkHandle } from './FixedProgramBulkTable';
import AiPresetOpenButton from './AiPresetOpenButton';
import type { FixedProgramDraft } from '../../utils/fixedProgramDrafts';
import type { ClassScheduleSettings, ProgramColorSettings } from '../../services/sectionLayoutService';

const KEY = 'cp-fpb-section-open';
function loadOpen(id: string): boolean {
  try {
    const m = JSON.parse(localStorage.getItem(KEY) || '{}') as Record<string, boolean>;
    return m[id] !== false;
  } catch {
    return true;
  }
}
function saveOpen(id: string, v: boolean) {
  try {
    const m = JSON.parse(localStorage.getItem(KEY) || '{}') as Record<string, boolean>;
    m[id] = v;
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* noop */
  }
}

/** 학사일정 섹션 맨 아래: 고정(행사) 프로그램 — 접기/펼치기 */
type SectionProps = {
  sectionKey: string;
  workspaceId: string;
  classOptions: BulkClassOption[];
  filterGradeIds: string[];
  /** 이 섹션 학년도(시작~종료). 1·2학기 행사를 클래스 필터와 함께 보여 준다. */
  includeDateRange?: { start: string; end: string };
  /** 페이지가 양쪽 학기 셀을 이미 맞출 때, 표가 한쪽 학기 캐시로 목록을 덮지 않게 */
  seedFromCellCache?: boolean;
  initialDrafts?: FixedProgramDraft[];
  initialSchedule?: ClassScheduleSettings | null;
  initialColors?: ProgramColorSettings | null;
  defaultOpen?: boolean;
  /** 페이지 '저장하기'(통합 저장)와 연동 */
  onDirtyChange?: (dirty: boolean) => void;
  /** 펼침 헤드의 AI 자료 입력 */
  onOpenAi?: () => void;
};
const FixedProgramSection = forwardRef<FixedProgramBulkHandle, SectionProps>(function FixedProgramSection({ sectionKey, defaultOpen, onOpenAi, classOptions, ...rest }, ref) {
  const [open, setOpen] = useState(() => defaultOpen ?? loadOpen(sectionKey));
  const [pickedClassIds, setPickedClassIds] = useState<string[]>([]);
  const toggle = () => {
    setOpen((v) => {
      saveOpen(sectionKey, !v);
      return !v;
    });
  };
  const registered = classOptions;
  const activeClassIds = pickedClassIds.filter((id) => registered.some((c) => c.id === id));
  const toggleClass = (id: string) => {
    setPickedClassIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  return (
    <section className={`cp-fpb-section cp-fpb-section--inner${open ? ' is-open' : ''}`} aria-label="고정(행사) 프로그램">
      <div className={`cp-fpb-toggle${onOpenAi ? ' has-ai' : ''}`}>
        <button type="button" className="cp-fpb-toggle-main" aria-expanded={open} onClick={toggle}>
          <span className="cp-fpb-toggle-title">고정(행사) 프로그램</span>
        </button>
        <div className="cp-fpb-toggle-side">
          {onOpenAi ? <AiPresetOpenButton onClick={onOpenAi} /> : null}
          <button type="button" className="cp-fpb-toggle-act" onClick={toggle}>
            {open ? '접기' : '펼치기'}
            <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden>
              <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      {/* 접어도 편집 중 내용이 사라지지 않도록 숨기기만 함 */}
      <div hidden={!open}>
        {registered.length > 0 ? (
          <div className="cp-fpb-class-filter" role="group" aria-label="적용클래스 필터">
            <span className="cp-fpb-class-filter-label">적용클래스</span>
            <button type="button" className={`cp-fpb-class-filter-btn${activeClassIds.length === 0 ? ' is-on' : ''}`} onClick={() => setPickedClassIds([])}>
              전체
            </button>
            {registered.map((c) => {
              const on = activeClassIds.includes(c.id);
              return (
                <button key={c.id} type="button" className={`cp-fpb-class-filter-btn${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggleClass(c.id)}>
                  <span className="cp-fixed-manage-class-block cp-fixed-manage-class-block-compact cp-cpk-class">
                    <span className="cp-grade-track-class-block-label">{c.label}</span>
                    {(c.grades ?? []).map((n) => (
                      <span key={n} className="cp-cpk-gnum">{n}</span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <FixedProgramBulkTable
          ref={ref}
          {...rest}
          classOptions={registered}
          strictSectionClasses
          activeClassIds={activeClassIds}
          emptyText={
            activeClassIds.length > 0
              ? '선택한 적용클래스의 고정 프로그램이 없어요.'
              : '이 섹션에 등록된 클래스의 고정 프로그램이 없어요. 헤드의 AI 자료 입력으로 행사 사진을 넣으면 찾아 드려요.'
          }
        />
      </div>
    </section>
  );
});

export default FixedProgramSection;
